import { useEffect, useState } from "react";
import { lineMaterialRows, type MaterialsLine } from "@workspace/api-zod/materials-list";
import { materialProposalBasis, materialProposalSchema, proposedMaterialsTotal, validMaterialAllowance,
  type MaterialAllowanceApproval, type ProposedMaterial } from "@workspace/api-zod/material-allowance";

const purchaseRows = (line: MaterialsLine): ProposedMaterial[] => {
  const snapshot = validMaterialAllowance(line);
  if (snapshot) return snapshot.materials ?? [];
  return lineMaterialRows(line, 0).filter(row => row.quantity > 0).map(row => ({
    name: row.name, specification: row.spec.slice(0, 300), quantity: row.quantity, unit: row.unit, unitPrice: row.unitPrice,
    assumption: "Existing recipe quantities and buy prices; confirm specification before ordering.",
  }));
};

export function PurchaseMaterialSummary({ line }: { line: MaterialsLine }) {
  const rows = lineMaterialRows(line, 0);
  const snapshot = validMaterialAllowance(line);
  if (!rows.length) return <p className="text-xs text-muted-foreground">
    {snapshot?.noMaterialsReason ?? (line.quoteCostRecipe ? "No supplied materials in this breakdown." : "Material not matched – please enter manually in Materials & Pricing Review.")}
  </p>;
  return <div className="text-xs font-normal">
    <div className="overflow-x-auto"><table className="w-full text-left">
      <thead className="text-muted-foreground"><tr>{["Material", "Qty", "Unit", "Buy price", "Cost"].map(title =>
        <th key={title} className="py-1 pr-2 font-normal">{title}</th>)}</tr></thead>
      <tbody>{rows.map(row => <tr key={row.key} className="border-t">
        <td className="py-1 pr-2">{row.name}</td><td className="py-1 pr-2 tabular-nums">{row.quantity}</td>
        <td className="py-1 pr-2">{row.unit}</td><td className="py-1 pr-2 tabular-nums">£{row.unitPrice.toFixed(2)}</td>
        <td className="py-1 tabular-nums">£{row.exactTotal.toFixed(2)}</td>
      </tr>)}</tbody>
    </table></div>
    <p className="border-t pt-1 mt-1">Suggested Materials Price £{(rows.reduce((total, row) => total + Math.round(row.exactTotal * 100), 0) / 100).toFixed(2)}</p>
    <details className="mt-1 text-muted-foreground"><summary className="cursor-pointer">Specifications &amp; assumptions</summary>
      {rows.map(row => <p className="py-1" key={row.key}>{row.name} · {row.spec}</p>)}
    </details>
  </div>;
}

/** Compact purchasing editor. No approval gate and no selling-price mutation. */
export function MaterialAllowanceControl({ line, onChange, status, onManualStart }: {
  line: MaterialsLine; onChange: (value: MaterialAllowanceApproval | null) => void;
  status?: string; onManualStart?: () => void;
}) {
  const snapshot = validMaterialAllowance(line);
  const fingerprint = JSON.stringify([materialProposalBasis(line), line.materialAllowanceApproval]);
  const [materials, setMaterials] = useState(() => purchaseRows(line));
  const [reason, setReason] = useState(snapshot?.noMaterialsReason ?? "");
  const [error, setError] = useState("");
  useEffect(() => { setMaterials(purchaseRows(line)); setReason(validMaterialAllowance(line)?.noMaterialsReason ?? ""); setError(""); }, [fingerprint]);
  const publish = (rows: ProposedMaterial[], noMaterialsReason?: string) => {
    onManualStart?.(); setMaterials(rows); setReason(noMaterialsReason ?? "");
    const parsed = materialProposalSchema.safeParse({ materials: rows, noMaterialsReason });
    if (!parsed.success) { setError("Enter a material name, positive quantity, unit and valid buy price."); return; }
    setError("");
    onChange({ version: 1, mode: "automatic", source: "builder", approved: false, basis: materialProposalBasis(line),
      amount: proposedMaterialsTotal(rows), ...parsed.data });
  };
  const change = (index: number, patch: Partial<ProposedMaterial>) => publish(materials.map((row, i) => i === index ? { ...row, ...patch } : row));
  return <details className="qm-purchase-editor" data-testid={`purchase-editor-${line.name}`}>
    <summary>Materials{materials.length ? ` · ${materials.length}` : ""}</summary>
    {status && <p role="status" className="qm-purchase-status">{status}</p>}
    {!materials.length && reason && <p className="qm-proposal-note">{reason}</p>}
    <div className="qm-purchase-head" aria-hidden="true"><span>Material</span><span>Qty</span><span>Unit</span><span>Buy price £</span><span /></div>
    {materials.map((material, index) => <div className="qm-purchase-row" key={index}>
      <input aria-label={`Material ${index + 1} for ${line.name}`} value={material.name} placeholder="Material name"
        onChange={e => change(index, { name: e.target.value })} />
      <input aria-label={`Material ${index + 1} quantity for ${line.name}`} type="number" min=".0001" step="any" value={material.quantity}
        onChange={e => change(index, { quantity: Number(e.target.value) })} />
      <input aria-label={`Material ${index + 1} unit for ${line.name}`} value={material.unit}
        onChange={e => change(index, { unit: e.target.value })} />
      <input aria-label={`Material ${index + 1} price for ${line.name}`} type="number" min="0" step=".01" value={material.unitPrice}
        onChange={e => change(index, { unitPrice: Number(e.target.value) })} />
      <button type="button" aria-label={`Remove material ${index + 1} from ${line.name}`}
        onClick={() => publish(materials.filter((_, i) => i !== index))}>×</button>
    </div>)}
    <div className="qm-purchase-footer">
      <button type="button" onClick={() => {
        onManualStart?.(); setMaterials(previous => [...previous, { name: "", specification: "", quantity: 1, unit: "each",
          unitPrice: 0, assumption: "Builder-entered purchase estimate." }]);
      }}>Add material</button>
      <span>Suggested Materials Price £{proposedMaterialsTotal(materials).toFixed(2)}</span>
    </div>
    <details className="qm-sources"><summary>Specifications &amp; assumptions</summary>
      {materials.map((material, index) => <div key={index}>
        <label>{material.name || `Material ${index + 1}`} · specification<input aria-label={`Material ${index + 1} specification for ${line.name}`}
          value={material.specification} onChange={e => change(index, { specification: e.target.value })} /></label>
        <label>Assumptions<input aria-label={`Material ${index + 1} assumption for ${line.name}`} value={material.assumption}
          onChange={e => change(index, { assumption: e.target.value })} /></label>
      </div>)}
      <p>Estimated quantities and buy prices; check specifications and pack sizes before ordering.</p>
      <button type="button" onClick={() => publish([], "Builder confirmed no supplied materials need purchasing.")}>No materials to buy</button>
    </details>
    {error && <p role="alert">{error}</p>}
  </details>;
}
